import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useI18n } from "../i18n";
import { Button } from "./ui/button";

const ARIOVIS_LOGO = "https://brand.ariovis.fr/logos/Logotype_Secondary_BLACK.svg";
const ARIOVIS_HOME = "https://ariovis.fr";
const ARIOVIS_PRODUCTS = "https://ariovis.fr/produits";

export default function Header() {
    const { lang, setLang, t } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();

    // Self-heal: clean up any stylesheet injected by the previous experimental
    // shell-fetching mode so lingering CSS from ariovis.fr does not affect UI.
    useEffect(() => {
        document
            .querySelectorAll("link[data-ariovis-shell]")
            .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
        try {
            localStorage.removeItem("ariovis-shell-v1");
        } catch (e) {
            /* ignore */
        }
    }, []);

    const switchLang = (nextLang) => {
        if (nextLang === lang) return;
        setLang(nextLang);
        const path = location.pathname.replace(/^\/(fr|en)(?=\/|$)/, `/${nextLang}`);
        navigate(path + location.search, { replace: false });
    };

    return (
        <header
            className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-sm"
            data-testid="site-header"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
                <div className="flex min-w-0 items-center gap-4">
                    <a
                        href={ARIOVIS_HOME}
                        className="flex items-center gap-3"
                        aria-label="Ariovis"
                        data-testid="header-logo-link"
                    >
                        <img
                            src={ARIOVIS_LOGO}
                            alt="Ariovis"
                            className="h-8 w-auto"
                            loading="eager"
                        />
                    </a>
                    <span className="hidden h-6 w-px bg-slate-200 sm:block" aria-hidden />
                    <Link
                        to={`/${lang}`}
                        className="hidden truncate text-sm font-semibold text-slate-950 hover:text-ariovis-bordeaux sm:inline"
                        data-testid="product-name"
                    >
                        {t("brand.product")}
                    </Link>
                </div>

                <nav className="flex items-center gap-2 sm:gap-4" aria-label="Navigation principale">
                    <Link
                        to={`/${lang}#method`}
                        className="hidden text-sm font-medium text-slate-700 hover:text-ariovis-bordeaux md:inline"
                        data-testid="nav-method"
                    >
                        {t("nav.method")}
                    </Link>
                    <Link
                        to={`/${lang}#faq`}
                        className="hidden text-sm font-medium text-slate-700 hover:text-ariovis-bordeaux md:inline"
                        data-testid="nav-faq"
                    >
                        {t("nav.faq")}
                    </Link>
                    <a
                        href={ARIOVIS_PRODUCTS}
                        className="hidden text-sm font-medium text-slate-700 hover:text-ariovis-bordeaux md:inline"
                        data-testid="nav-products"
                    >
                        {lang === "fr" ? "Produits Ariovis" : "Ariovis products"}
                    </a>
                    <a
                        href={ARIOVIS_HOME}
                        className="hidden text-sm font-medium text-slate-700 hover:text-ariovis-bordeaux md:inline-flex md:items-center md:gap-1"
                        data-testid="nav-home"
                    >
                        ariovis.fr
                        <ExternalLink className="h-3 w-3" aria-hidden />
                    </a>

                    <div
                        className="flex items-center gap-1 rounded-sm border border-slate-200 bg-white p-0.5 text-xs font-semibold"
                        role="group"
                        aria-label="Language selector"
                    >
                        <button
                            type="button"
                            onClick={() => switchLang("fr")}
                            className={`rounded-sm px-2 py-1 uppercase tracking-widest transition-colors ${
                                lang === "fr"
                                    ? "bg-slate-900 text-white"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                            data-testid="lang-switch-fr"
                            aria-pressed={lang === "fr"}
                        >
                            FR
                        </button>
                        <button
                            type="button"
                            onClick={() => switchLang("en")}
                            className={`rounded-sm px-2 py-1 uppercase tracking-widest transition-colors ${
                                lang === "en"
                                    ? "bg-slate-900 text-white"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                            data-testid="lang-switch-en"
                            aria-pressed={lang === "en"}
                        >
                            EN
                        </button>
                    </div>

                    <Button
                        asChild
                        size="sm"
                        className="rounded-sm bg-ariovis-bordeaux text-white hover:bg-ariovis-fuschia"
                        data-testid="header-cta"
                    >
                        <Link to={`/${lang}/wizard`}>{t("nav.start")}</Link>
                    </Button>
                </nav>
            </div>
        </header>
    );
}
